import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFetcher, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import _ from "lodash";
import { RiArrowRightLine, RiCheckboxCircleFill, RiCloseCircleFill } from "@remixicon/react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe, type PaymentIntent, type Stripe } from "@stripe/stripe-js";

import { useUser } from "routes/dataroutes/UserData";

import Card from "components/Card";
import PaymentIssuesGuardModal from "v2/components/billing/shared/PaymentIssuesGuardModal";
import Modal, { ModalButtonsSection, ModalContent, ModalSections } from "components/Modal";
import Spinner from "components/Spinner";
import SubmitButton from "components/SubmitButton";
import Button from "components/Button";
import CouponContent from "v2/components/billing/modals/upgrade-modal/CouponContent";

import { usePlanHelpers } from "routes/data-routes/billing-data/plans/usePlanHelpers";
import { useSubscriptionEstimate } from "v2/hooks/billing/useSubscriptionEstimate";

import {
  getMonthlyCredits,
  getMonthlyPriceInUSD,
  getPlanTier,
  isLegacyAnnualPlan,
  isYearlyPlan
} from "utils/planUtils";
import { cx, fmtCurrency, fmtNumber } from "utils";
import { trackBillingV2DowngradeSubmitted, trackBillingV2UpgradeSubmitted } from "utils/Tracking";

import Plan from "types/Plan";
import { Coupon } from "types/Coupons";
import { BillingInputName } from "v2/dataroutes/BillingData";
import ActiveSubscription from "types/ActiveSubscription";
import { ActiveSubscriptionFormActions } from "routes/data-routes/billing-data/active-subscription/ActiveSubscriptionFormActions";
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import {
  ActiveSubscriptionDataRoutePath
} from "routes/data-routes/billing-data/active-subscription/route.meta";
import useBillingInfo from "v2/hooks/billing/useBillingInfo";
import { PureTooltip } from "components/Tooltip";
import { CheckCouponsFetcherDataType } from "routes/data-routes/billing-data/coupons/check-coupons/route.meta";
import { useBillingNavigation } from "v2/pages/billing/hooks/useBillingNavigation";
import { BillingPath } from "v2/pages/billing/BillingPath";

const stripePromiseCache: Record<string, Promise<Stripe | null>> = {};

function getStripePromise(publishableKey: string) {
  if (!stripePromiseCache[publishableKey]) {
    stripePromiseCache[publishableKey] = loadStripe(publishableKey);
  }

  return stripePromiseCache[publishableKey];
}

const STRIPE_PAYMENT_ACTION_STORAGE_KEY = "scraperapi.stripe.subscription-payment";

type StripePaymentAction = NonNullable<ActiveSubscription["paymentAction"]>;
type RequiresActionStripePayment = Extract<StripePaymentAction, { status: "requires_action" }>;
type RequiresPaymentMethodStripePayment = Extract<StripePaymentAction, { status: "requires_payment_method" }>;
type PendingStripePaymentAction = RequiresActionStripePayment;
type StoredStripePaymentAction = {
  billingSubscriptionId: string;
  paymentAction: PendingStripePaymentAction;
};

function readPendingStripePaymentAction(): StoredStripePaymentAction | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const rawValue = window.sessionStorage.getItem(STRIPE_PAYMENT_ACTION_STORAGE_KEY);
    if (!rawValue) return undefined;

    const value = JSON.parse(rawValue) as Partial<StoredStripePaymentAction>;
    const paymentAction = value.paymentAction;
    if (
      typeof value.billingSubscriptionId !== "string"
      || paymentAction?.status !== "requires_action"
      || typeof paymentAction.clientSecret !== "string"
      || typeof paymentAction.publishableKey !== "string"
      || typeof paymentAction.targetPlanSlug !== "string"
    ) {
      return undefined;
    }

    return value as StoredStripePaymentAction;
  } catch {
    return undefined;
  }
}

function savePendingStripePaymentAction(paymentAction: PendingStripePaymentAction, billingSubscriptionId: string) {
  try {
    const storedPaymentAction: StoredStripePaymentAction = {
      billingSubscriptionId,
      paymentAction
    };
    window.sessionStorage.setItem(STRIPE_PAYMENT_ACTION_STORAGE_KEY, JSON.stringify(storedPaymentAction));
  } catch {
    // The server response remains authoritative; storage is only for redirect recovery.
  }
}

function clearPendingStripePaymentAction() {
  try {
    window.sessionStorage.removeItem(STRIPE_PAYMENT_ACTION_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function getStripePaymentIntentReturn() {
  if (typeof window === "undefined") return undefined;

  const url = new URL(window.location.href);
  const clientSecret = url.searchParams.get("payment_intent_client_secret");
  return clientSecret ? { clientSecret } : undefined;
}

function clearStripePaymentIntentReturnParams() {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  url.searchParams.delete("payment_intent");
  url.searchParams.delete("payment_intent_client_secret");
  url.searchParams.delete("redirect_status");
  window.history.replaceState(window.history.state, document.title, url.toString());
}

function StripePaymentActionContent(
  {
    paymentAction,
    billingSubscriptionId,
    isReturningFromStripe,
    onConfirmed,
    onCancel
  }: {
    paymentAction: RequiresActionStripePayment;
    billingSubscriptionId: string;
    isReturningFromStripe: boolean;
    onConfirmed: () => void;
    onCancel: () => void;
  }
) {
  const stripePromise = useMemo(
    () => getStripePromise(paymentAction.publishableKey),
    [ paymentAction.publishableKey ]
  );

  if (!stripePromise) {
    return (
      <ModalContent>
        <ModalSections>
          <p>Secure payment confirmation could not be loaded.</p>
        </ModalSections>
        <ConfirmationButtonSection />
      </ModalContent>
    );
  }

  return (
    <Elements
      key={ paymentAction.clientSecret }
      stripe={ stripePromise }
      options={ {
        clientSecret: paymentAction.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#0b4cff",
            borderRadius: "6px",
            fontFamily: "Work Sans, system-ui, sans-serif"
          }
        }
      } }
    >
      <StripePaymentActionFields
        paymentAction={ paymentAction }
        billingSubscriptionId={ billingSubscriptionId }
        isReturningFromStripe={ isReturningFromStripe }
        onConfirmed={ onConfirmed }
        onCancel={ onCancel }
      />
    </Elements>
  );
}

function StripePaymentActionFields({
  paymentAction,
  billingSubscriptionId,
  isReturningFromStripe,
  onConfirmed,
  onCancel
}: {
  paymentAction: RequiresActionStripePayment;
  billingSubscriptionId: string;
  isReturningFromStripe: boolean;
  onConfirmed: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [ submitting, setSubmitting ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState<string | undefined>();
  const recoveryAttemptedRef = useRef(false);

  const completePaymentIntent = useCallback(async (paymentIntent: PaymentIntent) => {
    if (paymentIntent.status !== "succeeded") {
      throw new Error("Your payment could not be confirmed.");
    }

    clearPendingStripePaymentAction();
    clearStripePaymentIntentReturnParams();
    onConfirmed();
  }, [ onConfirmed ]);

  useEffect(() => {
    if (!isReturningFromStripe || !stripe || recoveryAttemptedRef.current) return;

    recoveryAttemptedRef.current = true;
    setSubmitting(true);
    stripe.retrievePaymentIntent(paymentAction.clientSecret)
      .then(result => {
        if (result.error) {
          throw new Error(result.error.message || "Your payment could not be confirmed.");
        }
        if (!result.paymentIntent) {
          throw new Error("Stripe did not return a payment intent.");
        }
        return completePaymentIntent(result.paymentIntent);
      })
      .catch(error => {
        const message = error instanceof Error ? error.message : "Your payment could not be confirmed.";
        clearPendingStripePaymentAction();
        clearStripePaymentIntentReturnParams();
        setErrorMessage(message);
      })
      .finally(() => setSubmitting(false));
  }, [ completePaymentIntent, isReturningFromStripe, paymentAction.clientSecret, stripe ]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setSubmitting(true);
    setErrorMessage(undefined);
    savePendingStripePaymentAction(paymentAction, billingSubscriptionId);

    // TODO can't confirm the payment like this here :(
    try {
      // const result = await stripe.confirmPayment({
      //   elements,
      //   confirmParams: {
      //     return_url: window.location.href
      //   },
      //   redirect: "if_required"
      // });

      const result: any = {};

      if (result.error) {
        throw new Error(result.error.message || "Your payment could not be confirmed.");
      }

      if (!result.paymentIntent) {
        throw new Error("Stripe did not return a payment intent.");
      }
      await completePaymentIntent(result.paymentIntent);
    } catch (error) {
      clearPendingStripePaymentAction();
      setErrorMessage(error instanceof Error ? error.message : "Your payment could not be confirmed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalContent>
      <ModalSections>
        <p>Please confirm your payment to finish changing your subscription.</p>
        <form onSubmit={ handleSubmit } className="flex flex-col gap-y-4">
          <div data-testid="stripe-subscription-payment-element">
            <PaymentElement />
          </div>
          { errorMessage && <p className="text-sm text-error-700">{ errorMessage }</p> }
          <ModalButtonsSection>
            <Button
              type="submit"
              text={ submitting ? "Confirming..." : "Confirm payment" }
              className="w-full button button-primary"
              size="SM"
              disabled={ submitting || !stripe || !elements }
            />
            <Button
              text="Cancel"
              className="w-full button button-secondary"
              size="SM"
              onClick={ onCancel }
              disabled={ submitting }
            />
          </ModalButtonsSection>
        </form>
      </ModalSections>
    </ModalContent>
  );
}

function StripePaymentMethodFailureContent({
  paymentAction,
  onCancel
}: {
  paymentAction: RequiresPaymentMethodStripePayment;
  onCancel: () => void;
}) {
  return (
    <ModalContent>
      <ModalSections>
        <div className="flex flex-col items-center gap-y-4 text-center">
          <RiCloseCircleFill className="size-14 text-error-600" />
          <p className="text-lg font-semibold">Payment failed</p>
          <p className="text-sm text-gray-700">{ paymentAction.message }</p>
          <p className="text-sm text-gray-600">Your current plan has not changed.</p>
        </div>
      </ModalSections>
      <ModalButtonsSection>
        <Button
          text="Update payment method"
          href={ BillingPath + "/details#payment-method" }
          className="w-full button button-primary"
          size="SM"
        />
        <Button
          text="Keep my current plan"
          onClick={ onCancel }
          className="w-full button button-secondary"
          size="SM"
        />
      </ModalButtonsSection>
    </ModalContent>
  );
}


function CheckPaymentIssuesLayout({ children }: { children?: ReactNode }) {
  const user = useUser();

  if (user?.hasPaymentIssues) {
    return <PaymentIssuesGuardModal />;
  } else {
    return <>{ children }</>;
  }
}

function ConfirmationButtonSection() {
  const { goBack } = useBillingNavigation();

  return (
    <ModalButtonsSection>
      <Button
        text="Done"
        className="w-full button button-primary"
        size="SM"
        onClick={ goBack }
      />
    </ModalButtonsSection>
  );
}

function AlreadyOnTargetPlanContent({ targetPlanName }: { targetPlanName: string | undefined }) {
  const { goBack: closeModal } = useBillingNavigation();

  useEffect(() => {
    const timeoutId = setTimeout(closeModal, 3000);
    return () => clearTimeout(timeoutId);
  }, [ closeModal ]);

  return (
    <ModalContent>
      <ModalSections>
        <p>You are already on the { targetPlanName } plan. No changes to make.</p>
      </ModalSections>
      <ConfirmationButtonSection />
    </ModalContent>
  );
}

function SuccessfullyChangedContent({ upgraded, targetPlan, price }: { upgraded: boolean, targetPlan: Plan | undefined, price: string | undefined }) {
  const Title = ({ children }: { children: ReactNode }) => <p>{ children }</p>;
  const Value = ({ className, children }: { className?: string, children: ReactNode }) => <p className={ cx(className, "flex justify-end font-bold") }>{ children }</p>;

  return (
    <ModalContent>
      {  upgraded && (
        <>
          <p className="text-center">Your plan has been upgraded to <span className="font-bold">{ targetPlan?.planName }</span>.</p>

          <Card
            className="bg-primary-50 border-primary-100"
          >
            <div className="grid grid-cols-[1fr_1fr] gap-4 items-center text-sm">
              <Title>Plan</Title><Value>{ targetPlan?.planName }</Value>
              <Title>Billing</Title><Value>{ isYearlyPlan(targetPlan?.planSlug) ? "Annual" : "Monthly" }</Value>

              <p className="w-full h-px bg-primary-100 col-span-2" />

              <Title>Amount</Title><Value className="text-2xl font-semibold">{ price }</Value>
            </div>
          </Card>
        </>
      ) }
      { !upgraded && (
        <Card
          className="bg-success-50 border-success-100"
        >
          <p className="text-center">Your plan will downgrade to <span className="font-bold">{ targetPlan?.planName }</span> at the end of your current billing period.</p>
        </Card>
      ) }
      <ConfirmationButtonSection />
    </ModalContent>
  );
}

type FeatureDifference = {
  oldDescription: string;
  newDescription: string;
  kind: "gain" | "loss";
};

function FeatureDifferenceLine(featureDifference: FeatureDifference, index: number) {
  return (
    <div
      key={ index }
      className="flex items-center gap-2 text-sm text-neutral-700"
    >
      { (featureDifference.kind === "gain") && <RiCheckboxCircleFill className="size-5 text-green-600 flex-shrink-0" /> }
      { (featureDifference.kind === "loss") && <RiCloseCircleFill className="size-5 text-error-600 flex-shrink-0" /> }
      <div className={ cx("w-full items-center text-sm text-neutral-700", featureDifference.newDescription ? "grid grid-cols-[1fr_auto_1fr]" : "flex") }>
        <span>{ featureDifference.oldDescription }</span>
        { featureDifference.newDescription && (
          <>
            <RiArrowRightLine className="w-5 h-5 text-gray-400 mx-4" />
            <span className="flex justify-end">{ featureDifference.newDescription }</span>
          </>
        ) }
      </div>
    </div>
  )
}

function collectFeatureDifferences(sourcePlan: Plan | undefined, targetPlan: Plan | undefined): FeatureDifference[] {
  if (!sourcePlan || !targetPlan) {
    return [];
  }

  const sourcePlanTier = getPlanTier(sourcePlan.planSlug);
  const targetPlanTier = getPlanTier(targetPlan.planSlug);

  const featureDifferences: FeatureDifference[] = [];

  // API Credits
  const creditsDiff = getMonthlyCredits(targetPlan) - getMonthlyCredits(sourcePlan);
  if (creditsDiff !== 0) {
    featureDifferences.push({
      oldDescription: fmtNumber(getMonthlyCredits(sourcePlan)) + " API Credits / month",
      newDescription: fmtNumber(getMonthlyCredits(targetPlan)) + " API Credits / month",
      kind: creditsDiff > 0 ? "gain" : "loss"
    });
  }

  // Concurrent Threads
  const concurrentThreadsDiff = targetPlan.apiConcurrencyLimit - sourcePlan.apiConcurrencyLimit;
  if (concurrentThreadsDiff !== 0) {
    featureDifferences.push({
      oldDescription: fmtNumber(sourcePlan.apiConcurrencyLimit) + " Concurrent Threads",
      newDescription: fmtNumber(targetPlan.apiConcurrencyLimit) + " Concurrent Threads",
      kind: concurrentThreadsDiff > 0 ? "gain" : "loss"
    });
  }

  // API Geotargeting
  const sourceGeo = sourcePlanTier === "free" ? null : sourcePlan.apiGeotargeting;
  const targetGeo = targetPlanTier === "free" ? null : targetPlan.apiGeotargeting;
  if (sourceGeo !== targetGeo) {
    featureDifferences.push({
      oldDescription: sourceGeo === null ? "Limited geotargeting" : ("Geotargeting: " + (sourceGeo === "All Geotargeting" ? "Global" : "US & EU")),
      newDescription: targetGeo === null ? "Limited geotargeting" : ("Geotargeting: " + (targetGeo === "All Geotargeting" ? "Global" : "US & EU")),
      kind: sourceGeo === null ? "gain" : (targetGeo === "All Geotargeting" ? "gain" : "loss")
    });
  }

  // Crawler
  // TODO we don't have a plan/susbscription level setting for this
  const freeTierDiff = (targetPlanTier === "free" ? 0 : 1) - (sourcePlanTier === "free" ? 0 : 1);
  if (freeTierDiff !== 0) {
    featureDifferences.push({
      oldDescription: sourcePlanTier === "free" ? "Limited crawler access" : "Full crawler access",
      newDescription: targetPlanTier === "free" ? "Limited crawler access" : "Full crawler access",
      kind: freeTierDiff > 0 ? "gain" : "loss"
    });
  }

  // AI Parser limit
  const aiParserDiff = (targetPlan.parserLimit ?? 1) - (sourcePlan.parserLimit ?? 1);
  if (aiParserDiff !== 0) {
    featureDifferences.push({
      oldDescription: ((sourcePlan.parserLimit ?? 1) === 1) ? "1 AI Parser" : "Up to " + fmtNumber(sourcePlan.parserLimit ?? 1) + " AI Parsers",
      newDescription: ((targetPlan.parserLimit ?? 1) === 1) ? "1 AI Parser" : "Up to " + fmtNumber(targetPlan.parserLimit ?? 1) + " AI Parsers",
      kind: aiParserDiff > 0 ? "gain" : "loss"
    });
  }

  // Ultra premium
  if (freeTierDiff !== 0) {
    featureDifferences.push({
      oldDescription: sourcePlanTier === "free" ? "No premium" : "Ultra Premium",
      newDescription: targetPlanTier === "free" ? "No premium" : "Ultra Premium",
      kind: freeTierDiff > 0 ? "gain" : "loss"
    });
  }

  // Analytics history
  const analyticsDiff = (targetPlan.hasFullAnalytics ? 1 : 0) - (sourcePlan.hasFullAnalytics ? 1 : 0);
  if (analyticsDiff !== 0) {
    featureDifferences.push({
      oldDescription: sourcePlan.hasFullAnalytics ? "Unlimited analytics history" : "Limited analytics history",
      newDescription: targetPlan.hasFullAnalytics ? "Unlimited analytics history" : "Limited analytics history",
      kind: analyticsDiff > 0 ? "gain" : "loss"
    });
  }

  // Pay-as-you-go
  const payAsYouGoDiff = (targetPlan.payAsYouGoAllowed ? 1 : 0) - (sourcePlan.payAsYouGoAllowed ? 1 : 0);
  if (payAsYouGoDiff !== 0) {
    featureDifferences.push({
      oldDescription: sourcePlan.payAsYouGoAllowed ? "Pay as you go" : "No PAYG",
      newDescription: targetPlan.payAsYouGoAllowed ? "Pay as you go" : "No PAYG",
      kind: payAsYouGoDiff > 0 ? "gain" : "loss"
    });
  }

  return featureDifferences;
}

function FeaturesComparisonCard({ sourcePlan, targetPlan }: { sourcePlan: Plan | undefined, targetPlan: Plan | undefined }) {
  const { getPlanChangeKind } = usePlanHelpers();

  const changeKind = getPlanChangeKind(sourcePlan?.planSlug, targetPlan?.planSlug);

  const featureDifferences = collectFeatureDifferences(sourcePlan, targetPlan);

  const title = (featureDifferences.length === 0) ? "Switching to " + targetPlan?.planName : (
    changeKind === "upgrade" ? ("By upgrading to the " + targetPlan?.planName + " plan you gain access to:") : ("By downgrading to the " + targetPlan?.planName + " plan you lose access to:")
  );

  return (
    <Card
      title={ <span className="font-semibold">{ title }</span> }
      className={ changeKind === "upgrade" ? "bg-rebrandPurple-50 border-rebrandPurple-200" : "bg-warning-50 border-warning-200" }
    >
      { featureDifferences.length === 0 && (
        <FeatureDifferenceLine
          oldDescription={ "Same plan capacity with " + (isYearlyPlan(targetPlan?.planSlug) ? "annual" : "monthly") + " billing" }
          newDescription=""
          kind={ changeKind === "upgrade" ? "gain" : "loss" }
        />
      ) }
      { featureDifferences.map(FeatureDifferenceLine) }
    </Card>
  );
}

function useEstimate({ targetPlan, newCoupon, endOfTerm }: { targetPlan: Plan | undefined, newCoupon: Coupon | undefined, endOfTerm: boolean }) {
  const { estimate, isLoading, error } = useSubscriptionEstimate(targetPlan?.planSlug, { newCoupon, endOfTerm });

  const price = useMemo(() => {
    if (estimate?.invoiceEstimate) {
      return fmtCurrency(estimate.invoiceEstimate.amountDue / 100, estimate.invoiceEstimate.currencyCode, 2);
    }
  }, [ estimate?.invoiceEstimate ]);

  return { estimate, price, isLoading, error };
}

function PriceEstimate({ isLoading, price, error, endOfTerm }: { isLoading: boolean, price: string | undefined, error: string | null, endOfTerm: boolean }) {
  if (!isLoading && (error || !price)) {
    return (
      <div className="text-sm text-error-700">
        We couldn't calculate an authoritative invoice estimate. Please try again.
      </div>
    );
  }

  return (
    <div className="text-sm text-neutral-700">
      You will be invoiced{" "}
      { isLoading ? <Spinner size="XXS" className="inline" /> : <span className="font-bold">{ price }</span> }
      { endOfTerm ? " at the end of your current billing cycle." : " starting immediately." }
    </div>
  );
}

export default function ChangeSubscriptionModal() {
  const { planId: targetPlanSlug } = useParams();
  const user = useUser();
  const { activeSubscription, activeSubscriptionError, activeSubscriptionFetcher, reloadActiveSubscription } = useActiveSubscription();
  const { getPlan, getPlanChangeKind } = usePlanHelpers();
  // TODO `billingReadiness` is only used here, so it shouldn't really be part of the hook. should either go to a
  //  separate hook, or here in a simple `useMemo`
  const { billingReadiness } = useBillingInfo();
  const stripePlanChangesUnavailable = activeSubscription?.billingProvider === "stripe"
    && activeSubscription.capabilities?.canWriteStripe === false;
  const planChangeReadinessTooltip = stripePlanChangesUnavailable
    ? "Stripe plan changes are currently unavailable."
    : billingReadiness.tooltip;
  const { goBack: closeModal } = useBillingNavigation();
  const oldSubscription = useRef<ActiveSubscription | null | undefined>(activeSubscription);
  useEffect(() => {
    if (!oldSubscription.current) {
      oldSubscription.current = activeSubscription;
    }
  }, [ activeSubscription ]);

  const couponsFetcher = useFetcher<CheckCouponsFetcherDataType>();

  const targetPlan = getPlan(targetPlanSlug);
  const planChangeKind = getPlanChangeKind(oldSubscription.current?.planSlug, targetPlanSlug);

  const [ state, setState ] = useState<"success" | "invalid" | undefined>();
  const [ operationId ] = useState(() => uuidv4());
  const [ stripePaymentIntentReturn ] = useState(() => getStripePaymentIntentReturn());
  const [ storedStripePaymentAction ] = useState(() => readPendingStripePaymentAction());
  const pendingStripePaymentAction = storedStripePaymentAction
    && storedStripePaymentAction.billingSubscriptionId === activeSubscription?.billingSubscriptionId
    ? storedStripePaymentAction.paymentAction
    : undefined;
  useEffect(() => {
    if (
      activeSubscription?.billingSubscriptionId
      && storedStripePaymentAction
      && storedStripePaymentAction.billingSubscriptionId !== activeSubscription.billingSubscriptionId
    ) {
      clearPendingStripePaymentAction();
    }
  }, [ activeSubscription?.billingSubscriptionId, storedStripePaymentAction ]);
  const serverPaymentAction = (activeSubscriptionFetcher.data as { activeSubscription?: ActiveSubscription } | undefined)?.activeSubscription?.paymentAction;
  const paymentAction = [ serverPaymentAction, pendingStripePaymentAction ]
    .find(action => action?.targetPlanSlug === targetPlanSlug);
  const requiresActionPayment = paymentAction?.status === "requires_action" ? paymentAction : undefined;
  const requiresPaymentMethod = paymentAction?.status === "requires_payment_method" ? paymentAction : undefined;
  const [ paymentConfirmationPending, setPaymentConfirmationPending ] = useState(false);
  const [ confirmationPollCount, setConfirmationPollCount ] = useState(0);
  const [ confirmationError, setConfirmationError ] = useState<string | undefined>();
  const isReturningFromStripe = !!stripePaymentIntentReturn
    && !!requiresActionPayment
    && requiresActionPayment.clientSecret === stripePaymentIntentReturn.clientSecret;

  const closeAndReload = () => {
    clearPendingStripePaymentAction();
    clearStripePaymentIntentReturnParams();
    reloadActiveSubscription(false);
    closeModal();
  };

  useEffect(() => {
    if (!paymentConfirmationPending) return;

    const targetApplied = activeSubscription?.planSlug === targetPlanSlug
      || activeSubscription?.scheduledSubscription?.planSlug === targetPlanSlug;
    if (targetApplied) {
      setPaymentConfirmationPending(false);
      setState("success");
      return;
    }

    if (activeSubscriptionFetcher.state !== "idle") return;
    if (confirmationPollCount >= 5) {
      setConfirmationError("Your payment succeeded, but the subscription update is still processing. Close this dialog and refresh shortly.");
      return;
    }

    const timeoutId = setTimeout(() => {
      setConfirmationPollCount(count => count + 1);
      reloadActiveSubscription(false);
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [
    activeSubscription,
    activeSubscriptionFetcher.state,
    confirmationPollCount,
    paymentConfirmationPending,
    reloadActiveSubscription,
    targetPlanSlug
  ]);

  useEffect(() => {
    if (!state) {
      if (oldSubscription.current?.planSlug === targetPlanSlug) {
        setState("invalid");
      } else if (!paymentAction && ((activeSubscription?.planSlug === targetPlanSlug) || (activeSubscription?.scheduledSubscription?.planSlug === targetPlanSlug))) {
        // successfully changed
        setState("success");
      }
    }
  }, [ activeSubscription, paymentAction, targetPlanSlug, state ]);

  const headline = useMemo(() => {
    if (state === "invalid") {
      return "Already on the " + targetPlan?.planName + " plan";
    }

    if (state === "success") {
      return (
        <div className="flex flex-col items-center justify-center w-full gap-y-6">
          <RiCheckboxCircleFill className="size-16 text-green-600 dark:text-green-400 border-[8px] rounded-full border-green-100 bg-green-100" />
          <span className="text-2xl font-semibold">{ planChangeKind === "upgrade" ? "Successfully upgraded" : "Downgrade scheduled" }</span>
        </div>
      );
    }

    return _.capitalize(planChangeKind) + " my plan";

  }, [ state, targetPlan, planChangeKind ]);

  const { price, isLoading, error: estimateError } = useEstimate({ targetPlan, newCoupon: (!couponsFetcher.data || _.has(couponsFetcher.data.coupon, "error")) ? undefined : couponsFetcher.data.coupon as Coupon, endOfTerm: planChangeKind === "downgrade" });
  const estimateUnavailable = planChangeKind === "upgrade" && !isLoading && Boolean(estimateError || !price);
  const submitTooltip = planChangeReadinessTooltip
    || (estimateUnavailable ? "An authoritative invoice estimate is required before changing plans." : undefined);

  useEffect(() => {
    // try clearing the error state after closing the dialog
    // TODO this might not be necessary after the fetcher's data is now cached. double-check if keeping an error state
    //  in the fetcher will also keep the data blank, or if that's now successfully returned from the stale cache
    if (activeSubscriptionError) {
      return () => {
        reloadActiveSubscription(false);
      }
    }
  }, [ activeSubscriptionError, reloadActiveSubscription ]);

  const renderCouponContent = useMemo(() => {
    return (planChangeKind === "upgrade")
      && activeSubscription?.capabilities?.coupons
      && (user?.canUseAllCoupons || (user?.canUseCoupons && !isLegacyAnnualPlan(targetPlanSlug)));
  }, [ activeSubscription, planChangeKind, user, targetPlanSlug ]);


  if (!activeSubscription || !activeSubscriptionFetcher || !oldSubscription.current) {
    return null;
  }

  return (
    <CheckPaymentIssuesLayout>
      <Modal
        headline={ headline }
        onClose={ closeAndReload }
        className="!max-w-[600px]"
      >
        <>
          { (state === "invalid") && <AlreadyOnTargetPlanContent targetPlanName={ targetPlan?.planName } /> }

          { (state === "success") && <SuccessfullyChangedContent upgraded={ planChangeKind === "upgrade" } targetPlan={ targetPlan } price={ price} /> }

          { !state && paymentConfirmationPending && (
            <ModalContent>
              <ModalSections>
                { confirmationError
                  ? <p className="text-sm text-error-700">{ confirmationError }</p>
                  : <p className="flex items-center gap-x-2"><Spinner size="XXS" /> Confirming your subscription change...</p> }
              </ModalSections>
              { confirmationError && <ConfirmationButtonSection /> }
            </ModalContent>
          ) }

          { !state && !paymentConfirmationPending && requiresPaymentMethod && (
            <StripePaymentMethodFailureContent
              paymentAction={ requiresPaymentMethod }
              onCancel={ closeAndReload }
            />
          ) }

          { !state && !paymentConfirmationPending && requiresActionPayment && (
            <StripePaymentActionContent
              paymentAction={ requiresActionPayment }
              billingSubscriptionId={ activeSubscription.billingSubscriptionId }
              isReturningFromStripe={ isReturningFromStripe }
              onConfirmed={ () => {
                clearPendingStripePaymentAction();
                setConfirmationError(undefined);
                setConfirmationPollCount(0);
                setPaymentConfirmationPending(true);
                reloadActiveSubscription(false);
              } }
              onCancel={ closeAndReload }
            />
          ) }

          { !state && !paymentConfirmationPending && !paymentAction && (
            <activeSubscriptionFetcher.Form
              method="post"
              action={ ActiveSubscriptionDataRoutePath }
            >
              <ModalContent>
                <ModalSections>
                  <p>
                    You're currently on the{" "}
                    <span className="font-bold">{ oldSubscription.current.planName } (${ getMonthlyPriceInUSD(oldSubscription.current) }/month)</span>
                    {" "} plan
                  </p>

                  <FeaturesComparisonCard sourcePlan={ oldSubscription.current } targetPlan={ targetPlan } />

                  { (planChangeKind === "upgrade") && (
                    <>
                      { renderCouponContent && (
                        <CouponContent targetPlanSlug={ targetPlanSlug } needExtraForm={ false } couponsFetcher={ couponsFetcher } />
                      ) }

                      <PriceEstimate price={ price } isLoading={ isLoading } error={ estimateError } endOfTerm={ false }/>
                    </>
                  ) }
                  { !renderCouponContent && (
                    <input type="hidden" name={ BillingInputName.targetPlanSlug } value={ targetPlanSlug }/>
                  ) }
                  <input type="hidden" name={ BillingInputName.operationId } value={ operationId } />

                  { activeSubscriptionError && (
                    <div className="text-error-700 text-sm">
                      <Card
                        title={ <span className="font-semibold">{ activeSubscriptionError.meta?.title ?? "Error changing your subscription" }</span> }
                        className="bg-error-50 !border-error-100"
                        size="MD"
                      >
                        <span>{ activeSubscriptionError.message }</span>
                      </Card>
                    </div>
                  ) }

                </ModalSections>
                <ModalButtonsSection>
                  { !activeSubscriptionError && (
                    <PureTooltip content={ submitTooltip } clickable className="w-full">
                      <SubmitButton
                        text={ planChangeKind + " my plan" }
                        className={ cx("w-full button button-primary", planChangeKind === "downgrade" && "warning") }
                        size="SM"
                        fetchersToCheck={ [ activeSubscriptionFetcher ] }
                        disabled={ !!submitTooltip || (planChangeKind === "upgrade" && isLoading) }
                        onClick={ () => {
                          planChangeKind === "upgrade" ?
                            trackBillingV2UpgradeSubmitted({ current_plan: oldSubscription.current?.planSlug, target_plan: targetPlanSlug }) :
                            trackBillingV2DowngradeSubmitted({ current_plan: oldSubscription.current?.planSlug, target_plan: targetPlanSlug });
                          }
                        }
                      { ...(planChangeKind === "upgrade" ? ActiveSubscriptionFormActions.upgrade : ActiveSubscriptionFormActions.downgrade) }
                      />
                    </PureTooltip>
                  ) }
                  { activeSubscriptionError && (
                    <Button
                      text={ activeSubscriptionError.meta?.buttonText }
                      href={ activeSubscriptionError.meta?.redirectTo }
                      className="w-full button button-primary destructive"
                      size="SM"
                    />
                  ) }
                  <Button
                    text="Keep my current plan"
                    onClick={ closeAndReload }
                    className="w-full button button-secondary"
                    size="SM"
                  />
                </ModalButtonsSection>
              </ModalContent>
            </activeSubscriptionFetcher.Form>
          ) }
        </>
      </Modal>
    </CheckPaymentIssuesLayout>
  );
}
